import React, { useRef, useCallback, useMemo, useContext } from 'react';
import { withTranslation } from 'react-i18next';

import { activateUserDetailed } from '../actions/app-props';

import { startConversation } from '../services/chat-service';
import { getRoleLabel } from '../utils/user';

import { RolesAndPermissionsContext } from 'context/RolesAndPermissionsContext';

import UserContainer from '../containers/user-container';

import Tooltip from './tooltip';
import AvatarBase from './avatar-base';
import UserCard from 'components/UserCard';

const UserEntry = ({ id, color, avatar, initials, name, removed, role, pending, subtitle, subtitleIcon, t, onSubtitleClick, onClick }) => {
    const rolesAndPermissions = useContext(RolesAndPermissionsContext);

    const handleClick = useCallback(() => {
        onClick && onClick(id);
    }, [id, onClick]);

    const handleSubtitleClick = useCallback(() => {
        onSubtitleClick && onSubtitleClick(id);
    }, [id, onSubtitleClick]);

    return (
        <div className="user-entry" onClick={handleClick}>
            <div className="user-entry-head">
                <AvatarBase size="medium" color={color} image={avatar} text={initials} />
            </div>

            <div className="user-entry-body">
                <div className="user-entry-body-main">
                    { name }
                </div>

                {
                    subtitle ? (
                        <div className="user-entry-body-meta">
                            <span className={'subtitle' + (onSubtitleClick ? ' action' : '')} onClick={onSubtitleClick && handleSubtitleClick}>{subtitle} {subtitleIcon}</span>
                        </div>
                    ) : removed ? (
                        <div className="user-entry-body-meta">
                            { t('no longer in team') }
                        </div>
                    ) : (
                        role ? (
                            <div className="user-entry-body-meta">
                                { rolesAndPermissions?.getRoleLabel(role) }

                                { pending ? ` (${t('common:pending')})` : '' }
                            </div>
                        ) : null
                    )
                }
            </div>
        </div>
    )
}

const Avatar = React.memo(({
    className,
    size,
    empty,
    subtitle,
    subtitles,
    subtitleIcon,
    additional,
    tooltip,
    tooltipContent,
    tooltipPlacement,
    solidImage,
    border,
    text,
    showStatus,
    users,

    id,
    image,
    color,
    secondaryColor,
    initials,
    name,
    online,
    available,
    email,
    timezone,
    removed,
    loading,
    role,
    pending,
    currentUserId,

    t,

    onSubtitleClick,
    onClick
}) => {
    const tooltipCtrl = useRef(null);

    const handleClick = useCallback(() => {
        if (onClick) {
            onClick(id);
        } else {
            tooltipCtrl.current && tooltipCtrl.current.show(0);
        }
    }, [id, tooltipCtrl, onClick]);

    const handleUserProfileClick = useCallback(() => {
        tooltipCtrl.current && tooltipCtrl.current.hide();
    }, [tooltipCtrl]);

    const handleSubtitleClick = useCallback(() => {
        onSubtitleClick && onSubtitleClick(id);
    }, [id, onSubtitleClick]);

    const userIsOnline = useMemo(() => {
        if (available === false) {
            return false;
        }

        if (currentUserId == id) {
            return true;
        }

        return online;
    }, [online, available, currentUserId, id])

    const tooltipContentElement = useMemo(() => {
        if (tooltipContent) {
            return tooltipContent({ id, name });
        }

        return <UserCard
            id={id}
            image={image}
            color={color}
            foregroundColor={secondaryColor}
            initials={text}
            name={name}
            online={userIsOnline}
            available={available}
            subtitleIcon={subtitleIcon}
            additional={additional}
            email={email}
            subtitle={subtitle}
            timezone={timezone}
            removed={removed}
            role={role}
            pending={pending}
            onSubtitleClick={handleSubtitleClick}
            onUserProfileClick={handleUserProfileClick}
        />;
    }, [image, color, secondaryColor, text, name, userIsOnline, available, subtitleIcon, additional, email, subtitle, timezone, removed, role, id, pending, tooltipContent, handleSubtitleClick, handleUserProfileClick]);

    const avatarProps = useMemo(() => ({
        className,
        size,
        empty,
        solidImage,
        border,
        image,
        color,
        secondaryColor,
        text,
        dimmed: removed,
        showStatus: showStatus && !removed,
        online: userIsOnline,
        onClick: handleClick
    }), [className, size, empty, solidImage, border, image, color, secondaryColor, text, removed, showStatus, userIsOnline, handleClick]);

    if (loading) {
        return null;
    }

    if (tooltip && !removed) {
        return (
            <Tooltip
                type="popout"
                showDelay={200}
                provideController={tooltipCtrl}
                placement={tooltipPlacement}
                // preventHide={true}
                tooltip={tooltipContentElement}
            >
                <span className="avatar-wrap tooltip-trigger">
                    <AvatarBase {...avatarProps} />
                </span>
            </Tooltip>
        )
    }

    if (users) {
        return (
            <Tooltip
                type="popout"
                showDelay={200}
                placement="bottom"
                provideController={tooltipCtrl}
                tooltip={
                    <div className="popout-inner">
                        <div className="project-users-list simple">
                            {
                                users.map((userId, index) => (
                                    <UserContainer key={index} id={userId}>
                                        <UserEntry
                                            t={t}
                                            subtitle={subtitles && Array.isArray(subtitles) ? (subtitles.find(user => user.id === userId) || {}).subtitle : subtitles}
                                            subtitleIcon={subtitleIcon}
                                            onClick={onClick}
                                            onSubtitleClick={onSubtitleClick}
                                        />
                                    </UserContainer>
                                ))
                            }
                        </div>
                    </div>
                }
            >
                <span className="avatar-wrap tooltip-trigger">
                    <AvatarBase {...avatarProps} />
                </span>
            </Tooltip>
        )
    }

    return (
        <span className="avatar-wrap">
            <AvatarBase {...avatarProps} />
        </span>
    );

});

export default withTranslation(['user', 'common'])(Avatar);